import type { ComponentPropsWithoutRef } from 'react';
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';

import { isSSR, singleton } from '@mit/utils';
import {
  Dialog,
  DialogPortal,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogOverlay,
  DialogTrigger,
} from '@radix-ui/react-dialog';

import { cn } from '../utils/cn';
import { injectClassName } from '../utils/inject-class-name';
import {
  ScopedClassNameProvider,
  useScopedClassName,
} from '../utils/use-scoped-classnames';

export type CustomDialogProps = ComponentPropsWithoutRef<typeof Dialog> & {
  container?: HTMLElement;
  humanReadableClassname?: string;
};

type OmitUndefined<T> = Exclude<T, undefined>;
type ContentProps = ComponentPropsWithoutRef<typeof DialogContent> & {
  disableBackdropClose?: boolean;
};

type OnInteractOutsideEvent = Parameters<
  OmitUndefined<ContentProps['onInteractOutside']>
>[0];

const ModalContent = ({
  onInteractOutside,
  disableBackdropClose,
  className,
  ...props
}: ContentProps) => {
  const handleInteractOutside = (e: OnInteractOutsideEvent) => {
    if (disableBackdropClose) e.preventDefault();

    onInteractOutside?.(e);
  };

  const scopedClassName = useScopedClassName('content');

  return (
    <DialogContent
      className={cn(className, scopedClassName)}
      onInteractOutside={handleInteractOutside}
      {...props}
    />
  );
};

const createModalQueue = () => {
  let modalQueue = [] as string[];

  const signalDesireToChangeState = (id: string, open: boolean) => {
    if (open) {
      if (!modalQueue.includes(id)) {
        modalQueue.push(id);
        notify();
        return true;
      }
      return false;
    }
    const isLast = modalQueue[modalQueue.length - 1] === id;
    modalQueue = modalQueue.filter((mid) => mid !== id);
    notify();
    return !isLast;
  };

  const selectIsOpen = (id: string) => {
    const last = modalQueue[modalQueue.length - 1];
    return last === id;
  };

  const listeners = new Set<() => void>();

  const notify = () => {
    listeners.forEach((fn) => fn());
  };

  const subscribe = (fn: () => void) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  return {
    signalDesireToChangeState,
    selectIsOpen,
    subscribe,
  };
};

const globalModalQueue = singleton<ReturnType<typeof createModalQueue>>(
  'modal-queue',
  () => {
    return createModalQueue();
  },
);

function useCallbackRef<T extends (...args: unknown[]) => unknown>(
  callback: T | undefined,
): T {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  });

  // https://github.com/facebook/react/issues/19240
  return useMemo(() => ((...args) => callbackRef.current?.(...args)) as T, []);
}

const Root = ({
  defaultOpen,
  open: propOpen,
  onOpenChange: onOpenChangeProp,
  container,
  children,
  ...props
}: CustomDialogProps) => {
  const _container = useMemo(() => {
    if (isSSR) return null;

    return document.getElementById('site-builder-editor');
  }, []);

  const id = useId();

  const desiredState = propOpen ?? defaultOpen ?? false;

  const lastState = useRef(desiredState);

  // there is no trust in referential stability for callbacks from MIT
  const onOpenChangeStable = useCallbackRef(onOpenChangeProp);

  const triggerOnOpenChange = useCallback(
    (newState: boolean) => {
      if (newState !== lastState.current) {
        lastState.current = newState;
        onOpenChangeStable?.(newState);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      triggerOnOpenChange(globalModalQueue.signalDesireToChangeState(id, open));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );

  // handle prop change
  useLayoutEffect(() => {
    globalModalQueue.signalDesireToChangeState(id, desiredState);
  }, [desiredState, id]);

  const selector = useCallback(() => {
    const newState = globalModalQueue.selectIsOpen(id);
    triggerOnOpenChange(newState);
    return newState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const actualState = useSyncExternalStore(
    globalModalQueue.subscribe,
    selector,
    () => false,
  );

  return (
    <ScopedClassNameProvider base='mit-modal'>
      <Dialog open={actualState} onOpenChange={onOpenChange} {...props}>
        <DialogPortal container={container ?? _container}>
          {children}
        </DialogPortal>
      </Dialog>
    </ScopedClassNameProvider>
  );
};

const ModalOverlay = injectClassName(DialogOverlay, 'overlay');
const ModalTitle = injectClassName(DialogTitle, 'title');
const ModalDescription = injectClassName(DialogDescription, 'description');
const ModalClose = injectClassName(DialogClose, 'close');
const ModalTrigger = injectClassName(DialogTrigger, 'trigger');

export {
  Root as Modal,
  ModalOverlay,
  ModalContent,
  ModalTitle,
  ModalDescription,
  ModalClose,
  ModalTrigger,
};
